import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import PageLoader from '../../components/info/pageloader'

const GlobalContext = createContext()

export const GlobalProvider = ({ initData, children }) => {

  const { apiClient, deviceInfo, auth: initialAuth, maintenance: initialMaintenance } = initData

  const [auth, setAuth] = useState(initialAuth)
  const [showLoader, setShowLoader] = useState(false)
  const [loaderText, setLoaderText] = useState('')

  const [maintenance, setMaintenance] = useState(
    initialMaintenance || {
      active: false,
      message: '',
      loading: false
    }
  )

  const api = apiClient

  const pageLoader = {
    show: text => {
      setLoaderText(text)
      setShowLoader(true)
    },
    hide: () => { setShowLoader(false) }
  }

  const refreshMaintenanceStatus = useCallback(async () => {
    try {
      const res = await api.call('/get-maintenance-status')
      if (res && res.data) {
        setMaintenance({
          active: !!res.data.maintenance,
          message: res.data.message || '',
          loading: false
        })
      }
    } catch (err) {
      console.error("Failed to check maintenance status:", err)
    }
  }, [api])

  const toggleMaintenance = useCallback(async (enabled, message) => {
    pageLoader.show(enabled ? "Enabling Maintenance Mode..." : "Disabling Maintenance Mode...")
    try {
      const res = await api.call('/toggle-maintenance', { enabled, message })
      if (res && res.data) {
        setMaintenance({
          active: !!res.data.maintenance,
          message: res.data.message || '',
          loading: false
        })
        return res.data
      }
    } catch (err) {
      console.error("Failed to toggle maintenance mode:", err)
      throw err
    } finally {
      pageLoader.hide()
    }
  }, [api])

  // Listen for maintenance mode broadcast from API response interceptor
  useEffect(() => {
    const handleMaintenanceEvent = () => {
      setMaintenance(prev => ({ ...prev, active: true }))
    }
    window.addEventListener('maintenance-mode-active', handleMaintenanceEvent)
    return () => {
      window.removeEventListener('maintenance-mode-active', handleMaintenanceEvent)
    }
  }, [])

  return (
    <GlobalContext.Provider
      value={{
        auth,
        setAuth,
        deviceInfo,
        api,
        pageLoader,
        maintenance,
        setMaintenance,
        toggleMaintenance,
        refreshMaintenanceStatus
      }}
    >
      <>
        {showLoader && <PageLoader block text={loaderText} />}
        {children}
      </>
    </GlobalContext.Provider>
  )
}

export const useGlobal = () => {
  return useContext(GlobalContext)
}
